import { styled, css } from 'styled-components';
import { Media } from '../../../../../theme/Media';
import { ALIGN_TO_FLEX, CELL_PADDING_COMPACT, CELL_PADDING_DEFAULT, MOBILE_PADDING } from '../../constants';
import { actionCellStyles, checkboxCellStyles, dndCellStyles, specialCellCenter } from '../../mixins';
import { TableCellContentType } from '../../types';
export const CellTitle = styled.div `
  ${({ theme }) => theme.typography.textMedium}
  ${({ theme }) => theme.typography.weightDefault}
  flex-shrink: 0;

  @media ${Media.smMin} {
    display: none;
  }
`;
export const CellInner = styled.div `
  display: flex;
  flex-direction: column;
  // Allow the content to be narrower than its longest word so overflow-wrap can break it to fit a
  // squeezed track, instead of keeping min-content width and spilling into the next column.
  min-width: 0;

  ${({ $contentType }) => $contentType === TableCellContentType.LOADING &&
    css `
      justify-content: center;
      width: 100%;
    `}

  ${({ $contentType }) => $contentType === TableCellContentType.NO_PADDING &&
    css `
      width: 100%;
      height: 100%;
    `}

  @media ${Media.xsMax} {
    gap: 4px;
    justify-content: center;
    align-items: flex-end;
  }
`;
export const CellWrapper = styled.div `
  padding: ${({ $compact }) => ($compact ? CELL_PADDING_COMPACT : CELL_PADDING_DEFAULT)};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  // Standard overflow-wrap keeps a cell's minimum width at the longest WORD (not one character, which
  // word-break: break-word would force) — so squeezed/auto columns never collapse to letter stacking.
  // Long unbreakable tokens still break to avoid overflow; the breakWord prop opts into anywhere-breaking.
  overflow-wrap: break-word;

  ${({ $align }) => $align &&
    css `
      justify-content: ${ALIGN_TO_FLEX[$align]};
    `}
  ${({ theme, $textBold }) => $textBold && theme.typography.weightMedium}

  ${({ $fullRow, $colSpan }) => $fullRow
    ? css `
          grid-column: 1 / -1;
        `
    : $colSpan &&
        css `
          grid-column: span ${$colSpan};
        `}

  @media ${Media.smMin} {
    min-height: ${({ $compact }) => ($compact ? '36px' : '72px')};

    ${({ $breakWord }) => $breakWord &&
    css `
        overflow-wrap: anywhere;
      `}
    ${({ $noWrap }) => $noWrap &&
    css `
        white-space: nowrap;
      `}

    ${({ $contentType }) => ($contentType === TableCellContentType.DND ||
    $contentType === TableCellContentType.CHECKBOX ||
    $contentType === TableCellContentType.ACTION) &&
    specialCellCenter}

    ${({ $contentType, $compact }) => $contentType === TableCellContentType.DND && dndCellStyles($compact)}
    ${({ $contentType, $compact }) => $contentType === TableCellContentType.CHECKBOX && checkboxCellStyles($compact)}
    ${({ $contentType, $compact }) => $contentType === TableCellContentType.ACTION && actionCellStyles($compact)}

    ${({ $contentType }) => $contentType === TableCellContentType.LOADING &&
    css `
        width: 100%;
      `}

    ${({ $contentType }) => $contentType === TableCellContentType.NO_PADDING &&
    css `
        padding: 0;
      `}
  }

  @media ${Media.xsMax} {
    ${({ theme }) => theme.typography.textMedium}
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: ${({ $mobileVerticalAlign }) => ($mobileVerticalAlign === 'center' ? 'center' : 'flex-start')};
    min-height: 36px;
    height: unset;
    padding: ${MOBILE_PADDING};
    width: 100%;
    word-break: break-all;

    ${({ $contentType }) => $contentType === TableCellContentType.ACTION &&
    css `
        justify-content: flex-end;
      `}

    /* NO_PADDING is full-bleed desktop content; on mobile cards it collapses (height: 0) so it
       does not add a blank row. If a NO_PADDING cell must show content on mobile, give it explicit
       height instead of relying on the cell. */
    ${({ $contentType }) => $contentType === TableCellContentType.NO_PADDING &&
    css `
        padding: 0;
        height: 0;
        align-items: stretch;
      `}

    & + & {
      border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
    }
  }
`;
